/**
 * Polygonwerkzeug. Alles in Metern, Ebene beliebig (Grundriss oder Dachebene).
 */
import {} from './typen.js';
/** Vorzeichenbehaftete Flaeche. Positiv = gegen den Uhrzeigersinn. */
export function signierteFlaeche(ring) {
    let summe = 0;
    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        summe += a.x * b.y - b.x * a.y;
    }
    return summe / 2;
}
export function flaecheninhalt(ring) {
    return Math.abs(signierteFlaeche(ring));
}
export function umfang(ring) {
    let summe = 0;
    for (let i = 0; i < ring.length; i++) {
        summe += kantenlaenge(ring, i);
    }
    return summe;
}
/** Laenge der Kante, die bei Punkt i beginnt. */
export function kantenlaenge(ring, i) {
    // Modulo, damit ein veralteter Kantenindex nicht die ganze Planung reißt.
    const a = ring[((i % ring.length) + ring.length) % ring.length];
    const b = ring[(i + 1) % ring.length];
    return Math.hypot(b.x - a.x, b.y - a.y);
}
/** Flaechenschwerpunkt. Faellt die Flaeche zusammen, der Mittelwert der Punkte. */
export function schwerpunkt(ring) {
    const a = signierteFlaeche(ring);
    if (Math.abs(a) < 1e-12) {
        let x = 0;
        let y = 0;
        for (const p of ring) {
            x += p.x;
            y += p.y;
        }
        return { x: x / ring.length, y: y / ring.length };
    }
    let cx = 0;
    let cy = 0;
    for (let i = 0; i < ring.length; i++) {
        const p = ring[i];
        const q = ring[(i + 1) % ring.length];
        const kreuz = p.x * q.y - q.x * p.y;
        cx += (p.x + q.x) * kreuz;
        cy += (p.y + q.y) * kreuz;
    }
    return { x: cx / (6 * a), y: cy / (6 * a) };
}
export function huellkasten(ring) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of ring) {
        if (p.x < minX)
            minX = p.x;
        if (p.y < minY)
            minY = p.y;
        if (p.x > maxX)
            maxX = p.x;
        if (p.y > maxY)
            maxY = p.y;
    }
    return { minX, minY, maxX, maxY };
}
/** Strahlenverfahren. Punkte genau auf der Kante gelten als innen. */
export function punktInnerhalb(p, ring, toleranz = 1e-9) {
    for (let i = 0; i < ring.length; i++) {
        if (punktAufStrecke(p, ring[i], ring[(i + 1) % ring.length], toleranz))
            return true;
    }
    let innen = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const a = ring[i];
        const b = ring[j];
        const schneidet = a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
        if (schneidet)
            innen = !innen;
    }
    return innen;
}
export function punktAufStrecke(p, a, b, toleranz = 1e-9) {
    const kreuz = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
    const laenge = Math.hypot(b.x - a.x, b.y - a.y);
    if (laenge < toleranz)
        return Math.hypot(p.x - a.x, p.y - a.y) <= toleranz;
    if (Math.abs(kreuz) / laenge > toleranz)
        return false;
    const punktAuf = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / (laenge * laenge);
    return punktAuf >= -toleranz && punktAuf <= 1 + toleranz;
}
/**
 * Echter Schnitt zweier Strecken. Beruehrungen in den Endpunkten zaehlen nicht,
 * sonst meldet jedes Modul, das sauber an der Kante sitzt, einen Konflikt.
 */
export function streckenSchneidenSich(a1, a2, b1, b2) {
    const d = (a2.x - a1.x) * (b2.y - b1.y) - (a2.y - a1.y) * (b2.x - b1.x);
    if (Math.abs(d) < 1e-12)
        return false;
    const t = ((b1.x - a1.x) * (b2.y - b1.y) - (b1.y - a1.y) * (b2.x - b1.x)) / d;
    const u = ((b1.x - a1.x) * (a2.y - a1.y) - (b1.y - a1.y) * (a2.x - a1.x)) / d;
    const e = 1e-9;
    return t > e && t < 1 - e && u > e && u < 1 - e;
}
/**
 * Liegt das Vieleck vollstaendig im Ring?
 *
 * Eckpunkte innen allein genuegt bei einspringenden Ecken nicht — deshalb
 * zusaetzlich der Test, dass keine Kante die Umrandung kreuzt.
 */
export function vollstaendigInnerhalb(vieleck, ring) {
    for (const p of vieleck) {
        if (!punktInnerhalb(p, ring))
            return false;
    }
    for (let i = 0; i < vieleck.length; i++) {
        const a1 = vieleck[i];
        const a2 = vieleck[(i + 1) % vieleck.length];
        for (let j = 0; j < ring.length; j++) {
            if (streckenSchneidenSich(a1, a2, ring[j], ring[(j + 1) % ring.length]))
                return false;
        }
    }
    return true;
}
/** Wie punktInnerhalb, aber Punkte auf der Kante zaehlen als aussen. */
export function punktStrengInnerhalb(p, ring, toleranz = 1e-9) {
    for (let i = 0; i < ring.length; i++) {
        if (punktAufStrecke(p, ring[i], ring[(i + 1) % ring.length], toleranz))
            return false;
    }
    return punktInnerhalb(p, ring, toleranz);
}
/**
 * Ueberlappen sich zwei Vielecke?
 *
 * Beruehrung zaehlt nicht — ein Modul, das buendig an der Aussparung sitzt,
 * darf gesetzt werden, sonst faellt bei jedem Kamin eine Reihe zu viel weg.
 */
export function ueberlappen(a, b) {
    for (const p of a)
        if (punktStrengInnerhalb(p, b))
            return true;
    for (const p of b)
        if (punktStrengInnerhalb(p, a))
            return true;
    // Bündig nebeneinander und dabei teilweise übereinander: Keine Ecke liegt
    // echt innen und keine Kante kreuzt echt — aber die Mitte einer Kante oder
    // des ganzen Vielecks liegt im anderen. Ohne das galten zwei Module mit
    // gleicher Reihe und 40 cm Versatz als „berührend".
    for (const [eins, anderes] of [[a, b], [b, a]]) {
        for (let i = 0; i < eins.length; i++) {
            const p = eins[i];
            const q = eins[(i + 1) % eins.length];
            if (punktStrengInnerhalb({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }, anderes))
                return true;
        }
        const mitte = {
            x: eins.reduce((s, p) => s + p.x, 0) / eins.length,
            y: eins.reduce((s, p) => s + p.y, 0) / eins.length,
        };
        if (punktInnerhalb(mitte, eins) && punktStrengInnerhalb(mitte, anderes))
            return true;
    }
    for (let i = 0; i < a.length; i++) {
        const a1 = a[i];
        const a2 = a[(i + 1) % a.length];
        for (let j = 0; j < b.length; j++) {
            if (streckenSchneidenSich(a1, a2, b[j], b[(j + 1) % b.length]))
                return true;
        }
    }
    return false;
}
export function drehe(p, winkelBogen, um = { x: 0, y: 0 }) {
    const c = Math.cos(winkelBogen);
    const s = Math.sin(winkelBogen);
    const dx = p.x - um.x;
    const dy = p.y - um.y;
    return { x: um.x + dx * c - dy * s, y: um.y + dx * s + dy * c };
}
export function dreheRing(ring, winkelBogen, um = { x: 0, y: 0 }) {
    return ring.map((p) => drehe(p, winkelBogen, um));
}
/**
 * Ring um einen Faktor um den Schwerpunkt strecken.
 *
 * Das ist die Rechnung hinter dem Referenzmass (PV-D15): Der Monteur misst eine
 * Kante mit dem Laser nach, das Polygon zieht sich darauf. Damit muss das
 * Luftbild nur noch die Form richtig hergeben, nicht die absolute Groesse.
 */
export function skaliere(ring, faktor, um) {
    const zentrum = um ?? schwerpunkt(ring);
    return ring.map((p) => ({
        x: zentrum.x + (p.x - zentrum.x) * faktor,
        y: zentrum.y + (p.y - zentrum.y) * faktor,
    }));
}
export function skaliereAufReferenzmass(ring, mass) {
    const ist = kantenlaenge(ring, mass.kanteIndex);
    if (ist < 1e-9)
        throw new Error('skaliereAufReferenzmass: Kante hat keine Länge');
    if (mass.laengeM <= 0)
        throw new Error('skaliereAufReferenzmass: Länge muss positiv sein');
    return skaliere(ring, mass.laengeM / ist);
}
/**
 * Ring an einem Rechteck beschneiden (Sutherland-Hodgman).
 *
 * Damit entsteht aus Flaeche plus Randabstaenden die tatsaechlich belegbare
 * Flaeche. Der Ring darf einspringende Ecken haben, der Beschnittbereich nicht.
 */
export function beschneideAmKasten(ring, kasten) {
    const kanten = [
        (p) => p.x - kasten.minX,
        (p) => kasten.maxX - p.x,
        (p) => p.y - kasten.minY,
        (p) => kasten.maxY - p.y,
    ];
    let ergebnis = ring;
    for (const innenAbstand of kanten) {
        if (ergebnis.length === 0)
            return [];
        const eingabe = ergebnis;
        ergebnis = [];
        for (let i = 0; i < eingabe.length; i++) {
            const akt = eingabe[i];
            const vor = eingabe[(i + eingabe.length - 1) % eingabe.length];
            const dAkt = innenAbstand(akt);
            const dVor = innenAbstand(vor);
            if (dAkt >= 0) {
                if (dVor < 0)
                    ergebnis.push(mische(vor, akt, dVor / (dVor - dAkt)));
                ergebnis.push(akt);
            }
            else if (dVor >= 0) {
                ergebnis.push(mische(vor, akt, dVor / (dVor - dAkt)));
            }
        }
    }
    return ergebnis;
}
function mische(a, b, t) {
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
/**
 * Ring an einer Geraden abschneiden. Zurueck kommt die Haelfte, die auf der
 * Seite liegt, in die die Normale zeigt.
 *
 * Damit wird der Gebaeudeumriss an der Firstlinie in zwei Dachflaechen geteilt.
 */
export function beschneideAnHalbebene(ring, aufDerLinie, normale) {
    const laenge = Math.hypot(normale.x, normale.y);
    if (laenge < 1e-12)
        throw new Error('beschneideAnHalbebene: Normale hat keine Länge');
    const n = { x: normale.x / laenge, y: normale.y / laenge };
    const abstand = (p) => (p.x - aufDerLinie.x) * n.x + (p.y - aufDerLinie.y) * n.y;
    const ergebnis = [];
    for (let i = 0; i < ring.length; i++) {
        const akt = ring[i];
        const vor = ring[(i + ring.length - 1) % ring.length];
        const dAkt = abstand(akt);
        const dVor = abstand(vor);
        if (dAkt >= 0) {
            if (dVor < 0)
                ergebnis.push(mische(vor, akt, dVor / (dVor - dAkt)));
            ergebnis.push(akt);
        }
        else if (dVor >= 0) {
            ergebnis.push(mische(vor, akt, dVor / (dVor - dAkt)));
        }
    }
    return ergebnis;
}
/** Achsparalleles Rechteck als Ring, gegen den Uhrzeigersinn. */
export function rechteck(x, y, breite, hoehe) {
    return [
        { x, y },
        { x: x + breite, y },
        { x: x + breite, y: y + hoehe },
        { x, y: y + hoehe },
    ];
}
/**
 * Ring nach aussen aufweiten, kantenparallel — fuer Sicherheitsabstaende.
 *
 * Jede Kante wandert um den Abstand nach aussen, die Ecken liegen im
 * Schnittpunkt der verschobenen Nachbarkanten. Vorher wurde jede Ecke vom
 * Schwerpunkt weg geschoben: An den Ecken stimmte der Abstand, an den Kanten
 * eines quadratischen Dachfensters blieben von 30 cm nur 21 — Module kamen
 * naeher heran als eingestellt.
 *
 * Fuer die ueblichen Aussparungen (Rechteck, Vieleck ohne einspringende Ecke)
 * exakt. Bei einer sehr spitzen Ecke wird der Schnittpunkt auf das Dreifache
 * des Abstands begrenzt, damit keine Spitze ins Dach hinaussticht.
 */
export function weiteAuf(ring, abstandM) {
    if (abstandM <= 0 || ring.length < 3)
        return ring;
    const aussen = signierteFlaeche(ring) > 0 ? 1 : -1;
    const n = ring.length;
    // Nach aussen zeigende Einheitsnormale je Kante i -> i+1.
    const normale = ring.map((a, i) => {
        const b = ring[(i + 1) % n];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const l = Math.hypot(dx, dy);
        return l < 1e-12 ? { x: 0, y: 0 } : { x: (aussen * dy) / l, y: (aussen * -dx) / l };
    });
    return ring.map((p, i) => {
        const vor = normale[(i + n - 1) % n];
        const nach = normale[i];
        // Die Ecke wandert entlang der Winkelhalbierenden, so weit, dass beide
        // Kanten genau den Abstand halten: d / cos(halber Knickwinkel).
        const hx = vor.x + nach.x;
        const hy = vor.y + nach.y;
        const hl = Math.hypot(hx, hy);
        if (hl < 1e-9)
            return { x: p.x + nach.x * abstandM, y: p.y + nach.y * abstandM };
        const cosHalb = (vor.x * nach.x + vor.y * nach.y + 1) / 2;
        const faktor = Math.min(3, 1 / Math.sqrt(Math.max(cosHalb, 1e-9)));
        return { x: p.x + (hx / hl) * abstandM * faktor, y: p.y + (hy / hl) * abstandM * faktor };
    });
}
//# sourceMappingURL=polygon.js.map